import { useState } from "react";
import { useModal } from "../../../hooks/useModal";
import {
  CommonCodeSearchType,
  CommonCodeSortKey,
  useDeleteCommonCodeAsAdminMutation,
  useGetCommonCodesAsAdminQuery,
} from "../../../lib/apollo/graphql/generated";
import { FETCH_SIZE } from "../../../utilities/constants";
import { formatDate } from "../../../utilities/format";
import BasicButton from "../../common/components/BasicButton";
import BoxWithShadow from "../../common/components/BoxWithShadow";
import ListPageTopSection from "../../common/components/ListPageTopSection";
import SearchBar from "../../common/components/SearchBar";
import SelectBox from "../../common/components/SelectBox";
import Table from "../../common/components/Table";
import { CodeGroup } from "../container/CodeContainer";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { SortDirection } from "global";
import UpdateCodeModal from "./UpdateCodeModal";
import ActiveStatus from "../../common/components/ActiveStatus";

type QueryFilter = {
  query: string;
  commonCodeSearchType: CommonCodeSearchType;
  sortDirection: SortDirection;
  sortKey: CommonCodeSortKey;
};
interface CodeBoxProps {
  group: CodeGroup | null;
  onCancel: () => void;
}

function NoGroupFallback() {
  return (
    <p className="flex flex-1 items-center justify-center text-lg text-zinc-500">
      공통코드 그룹을 선택해 주세요.
    </p>
  );
}

export default function CodeBox({ group, onCancel }: CodeBoxProps) {
  const [
    isUpdateModalOpen,
    { open: openUpdateModal, close: closeUpdateModal },
  ] = useModal();

  const [selectedCodeId, setSelectedCodeId] = useState<string | null>(null);
  const [filters, setFilters] = useState<QueryFilter>({
    sortKey: CommonCodeSortKey.CreatedAt,
    sortDirection: "DESC",
    query: "",
    commonCodeSearchType: CommonCodeSearchType.Name,
  });

  const onFilterChange = <K extends keyof QueryFilter>(
    key: K,
    value: QueryFilter[K]
  ) => {
    setFilters((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const isGroupSelected = !!group?.name && !!group?.id;

  const { data, loading, refetch } = useGetCommonCodesAsAdminQuery({
    variables: {
      commonCodeGroupId: group?.id!,
      first: FETCH_SIZE,
    },
    skip: !group?.id,
  });

  const [deleteCode] = useDeleteCommonCodeAsAdminMutation({
    onCompleted: () => window.alert("코드를 삭제했습니다"),
    refetchQueries: ["GetCommonCodesAsAdmin"],
  });

  const onSearch = () => {
    refetch({
      first: FETCH_SIZE,
      ...filters,
    });
  };

  const onUpdate = (codeId: string | null) => {
    setSelectedCodeId(codeId);
    openUpdateModal();
  };

  const onDelete = (codeId: string) => {
    const confirmed = window.confirm("삭제하시겠습니까?");

    if (!confirmed) {
      return;
    }

    deleteCode({
      variables: {
        commonCodeId: codeId,
      },
    });
  };

  const onSort = (key?: CommonCodeSortKey, direction?: SortDirection) => {
    const updatedFilters: QueryFilter = {
      ...filters,
      sortKey: !key ? CommonCodeSortKey.CreatedAt : key,
      sortDirection: !direction ? "DESC" : direction,
    };

    setFilters(updatedFilters);
    refetch({
      ...updatedFilters,
    });
  };

  const codes = data?.getCommonCodesAsAdmin.edges || [];

  return (
    <>
      {isUpdateModalOpen && (
        <UpdateCodeModal
          codeId={selectedCodeId}
          open={isUpdateModalOpen}
          onClose={closeUpdateModal}
        />
      )}
      <BoxWithShadow className="gap-3">
        <div className="flex items-center gap-3">
          <p className="font-bold">
            코드 관리{" "}
            <span className={!isGroupSelected ? "text-zinc-300" : ""}>
              ({group?.name ? `${group?.name} / ${group.code}` : "그룹 미선택"})
            </span>
          </p>
          {!!group && (
            <XMarkIcon
              onClick={onCancel}
              className="ml-auto h-5 w-5 cursor-pointer rounded-md border border-zinc-300 bg-zinc-100"
            />
          )}
        </div>
        {!group ? (
          <NoGroupFallback />
        ) : (
          <>
            <ListPageTopSection>
              <SelectBox
                value={filters.commonCodeSearchType}
                onSelect={(type) =>
                  onFilterChange("commonCodeSearchType", type)
                }
                options={[
                  {
                    label: "코드명",
                    value: CommonCodeSearchType.Name,
                  },
                  {
                    label: "코드",
                    value: CommonCodeSearchType.Code,
                  },
                ]}
              />
              <SearchBar
                placeholder="검색 내용"
                value={filters.query}
                onSearch={onSearch}
                onChange={(value) => onFilterChange("query", value)}
              />
              <div className="ml-auto">
                <BasicButton text="코드 추가" onClick={() => onUpdate(null)} />
              </div>
            </ListPageTopSection>
            <Table.Wrapper>
              <Table.Header
                tabs={[
                  {
                    label: "코드",
                    sortKey: CommonCodeSortKey.Code,
                    className: "w-1/4 flex-shrink-0",
                  },
                  {
                    label: "코드명",
                    sortKey: CommonCodeSortKey.Name,
                    className: "w-1/4 flex-shrink-0",
                  },
                  {
                    label: "상태",
                    sortKey: CommonCodeSortKey.Status,
                  },
                  {
                    label: "등록일자",
                    sortKey: CommonCodeSortKey.CreatedAt,
                  },
                  { label: "관리" },
                ]}
                sort={{
                  key: filters.sortKey,
                  direction: filters.sortDirection,
                  onSort,
                }}
              />
              <Table.Body isLoading={loading} isEmpty={!codes?.length}>
                {codes?.map((code) => {
                  return (
                    <Table.Row>
                      <Table.Column className="line-clamp-1 w-1/4 flex-shrink-0 text-ellipsis text-center">
                        {code.code}
                      </Table.Column>
                      <Table.Column className="line-clamp-1 w-1/4 flex-shrink-0 text-ellipsis text-center">
                        {code.name}
                      </Table.Column>
                      <Table.Column className="w-[40%]">
                        <ActiveStatus status={code.status} />
                      </Table.Column>
                      <Table.Column className="w-[40%]">
                        {formatDate(code.createdAt, "YYYY.MM.DD")}
                      </Table.Column>
                      <Table.RowManageButtons
                        className="w-[40%]"
                        onUpdate={() => onUpdate(code.id)}
                        onDelete={
                          code.status === "INACTIVE"
                            ? undefined
                            : () => onDelete(code.id)
                        }
                      />
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Wrapper>
          </>
        )}
      </BoxWithShadow>
    </>
  );
}
